import { CampaignRoutingCondition } from "@signalist/shared-types";

import { fieldLabelKey, operatorLabelKey, ROUTING_I18N } from "./campaignRoutingFields";

/** The `t` the text helpers need: a key and its interpolation values in, the sentence out. */
export type RoutingTranslate = (key: string, options?: Record<string, unknown>) => string;

const betweenText = (
	condition: CampaignRoutingCondition,
	field: string,
	t: RoutingTranslate
): string => {
	const from = condition.from ?? null;
	const to = condition.to ?? null;

	if (from !== null && to !== null) {
		return t(`${ROUTING_I18N}.condition.between`, { field, from, to });
	}

	if (from !== null) {
		return t(`${ROUTING_I18N}.condition.atLeast`, { field, from });
	}

	return t(`${ROUTING_I18N}.condition.atMost`, { field, to: to ?? "" });
};

/** One condition as a sentence: "Personal country is any of Germany, Austria". */
export const conditionText = (condition: CampaignRoutingCondition, t: RoutingTranslate): string => {
	const field = t(fieldLabelKey(condition.field));

	if (condition.operator === "BETWEEN") {
		return betweenText(condition, field, t);
	}

	const operator = t(operatorLabelKey(condition.operator));
	// Shown as stored: a Profile language holds the name the API compares ("German"), not a code.
	const values = condition.values ?? [];

	if (values.length === 0) {
		return t(`${ROUTING_I18N}.condition.empty`, { field, operator });
	}

	return t(`${ROUTING_I18N}.condition.values`, {
		field,
		operator,
		values: values.join(t(`${ROUTING_I18N}.condition.valueSeparator`)),
	});
};

/** A rule's conditions joined as the evaluator reads them: every one must hold ("AND"). */
export const conditionsText = (
	conditions: readonly CampaignRoutingCondition[],
	t: RoutingTranslate
): string =>
	conditions
		.map((condition) => conditionText(condition, t))
		.join(t(`${ROUTING_I18N}.condition.and`));

/** One summary line: "If Personal country is any of Germany, Austria → DACH Outreach". */
export const ruleSummaryText = (
	conditions: readonly CampaignRoutingCondition[],
	campaignName: string,
	t: RoutingTranslate
): string =>
	t(`${ROUTING_I18N}.summary.ruleLine`, {
		conditions: conditionsText(conditions, t),
		campaign: campaignName,
	});
